import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  BLOOD_GROUPS, OPIATE_SUBSTANCES, OPIATE_UNITS, SEXES, SUBSTANCE_LABEL,
} from '@pharmacia/core'
import { ApiError, get, patch } from '../lib/api.ts'
import type { UploadedFile } from '../lib/upload.ts'
import { PageHeader } from '../components/AppShell.tsx'
import { CameraCapture } from '../components/CameraCapture.tsx'
import { useToast } from '../components/Toast.tsx'
import { Button, Field, Input, Select, Skeleton } from '../components/ui.tsx'

type Str = Record<string, string>
const num = (v: string) => (v.trim() === '' ? null : Number(v))
const txt = (v: string) => (v.trim() === '' ? null : v.trim())
const str = (v: unknown) => (v === null || v === undefined ? '' : String(v))

/**
 * Edits the whole record, not just the demographics: identity, the reading
 * taken at registration, the drug quantity and the referral details.
 *
 * Everything saves in ONE request and one database transaction. Three
 * separate saves would leave a statutory register half-updated when one of
 * them failed, which is worse than rejecting the change outright.
 */
export function EditRecordPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [versions, setVersions] = useState({ patient: 0, registration: 0 })
  const [regId, setRegId] = useState<string | null>(null)
  const [patient, setPatient] = useState<Str>({})
  const [measure, setMeasure] = useState<Str>({})
  const [drug, setDrug] = useState<Str>({})
  const [record, setRecord] = useState<Str>({})
  // Only images the operator re-captures are sent; the rest stay as they
  // are rather than being re-uploaded on every save.
  const [photo, setPhoto] = useState<UploadedFile | null>(null)
  const [aadhaarFront, setAadhaarFront] = useState<UploadedFile | null>(null)
  const [aadhaarBack, setAadhaarBack] = useState<UploadedFile | null>(null)
  const [existing, setExisting] = useState<Record<string, string>>({})

  useEffect(() => {
    let cancelled = false
    get<{
      patient: Record<string, unknown>
      registrations: Record<string, unknown>[]
      measurements: Record<string, unknown>[]
      files: { id: string; kind: string }[]
    }>(`/api/patients/${id}`)
      .then(d => {
        if (cancelled) return
        const p = d.patient
        const r = d.registrations[0]
        const m = d.measurements.find(x => Number(x['day_offset']) === 0)
        setPatient({
          fullName: str(p['fullName']), fatherName: str(p['fatherName']), age: str(p['age']),
          sex: str(p['sex']), maritalStatus: str(p['maritalStatus']), education: str(p['education']),
          caste: str(p['caste']), occupation: str(p['occupation']), phone: str(p['phone']),
          addressLine1: str(p['addressLine1']), tehsil: str(p['tehsil']), district: str(p['district']),
          state: str(p['state']), pincode: str(p['pincode']), bloodGroup: str(p['bloodGroup']),
          aadhaar: str(p['aadhaar']),
        })
        setMeasure({
          weightKg: str(m?.['weight_kg']), bpSystolic: str(m?.['bp_systolic']),
          bpDiastolic: str(m?.['bp_diastolic']), pulse: str(m?.['pulse']), spo2: str(m?.['spo2']),
        })
        setDrug({
          opiateAmount: str(r?.['opiate_amount']), opiateUnit: str(r?.['opiate_unit']) || 'g',
          opiateSubstance: str(r?.['opiate_substance']) || 'opium', tablets: str(r?.['tablets']),
          addictionYears: str(r?.['addiction_years']), addictionMonths: str(r?.['addiction_months']),
        })
        setRecord({ remarks: str(r?.['remarks']) })
        // Existing images are shown as thumbnails via a signed link; a
        // new capture replaces one only when the operator takes it.
        void Promise.all(d.files.map(async f => {
          try {
            const { url } = await get<{ url: string }>(`/api/files/${f.id}`)
            if (!cancelled) setExisting(prev => ({ ...prev, [f.kind]: url }))
          } catch { /* the record is still editable without it */ }
        }))
        setRegId(r ? String(r['id']) : null)
        setVersions({ patient: Number(p['version']), registration: Number(r?.['version'] ?? 0) })
      })
      .catch((e: ApiError) => { if (!cancelled) toast.fail('Could not load record', e.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id, toast])

  const bind = (state: Str, set: (s: Str) => void) => (k: string) =>
    ({ value: state[k] ?? '', onChange: (e: { target: { value: string } }) => set({ ...state, [k]: e.target.value }) })
  const p = bind(patient, setPatient)
  const m = bind(measure, setMeasure)
  const d = bind(drug, setDrug)
  const r = bind(record, setRecord)

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!regId) { toast.fail('This patient has no register entry to edit'); return }
    setBusy(true)
    setErrors({})
    try {
      await patch(`/api/registrations/${regId}`, {
        patient: {
          fullName: patient['fullName']!.trim(), fatherName: txt(patient['fatherName']!),
          age: num(patient['age']!), sex: txt(patient['sex']!), maritalStatus: txt(patient['maritalStatus']!),
          education: txt(patient['education']!), caste: txt(patient['caste']!),
          occupation: txt(patient['occupation']!), phone: txt(patient['phone']!),
          addressLine1: txt(patient['addressLine1']!), tehsil: txt(patient['tehsil']!),
          district: txt(patient['district']!), state: txt(patient['state']!),
          pincode: txt(patient['pincode']!), bloodGroup: txt(patient['bloodGroup']!),
          aadhaar: patient['aadhaar']!.trim(),
        },
        measurement: {
          weightKg: num(measure['weightKg']!), bpSystolic: num(measure['bpSystolic']!),
          bpDiastolic: num(measure['bpDiastolic']!), pulse: num(measure['pulse']!),
          spo2: num(measure['spo2']!),
        },
        drug: {
          opiateAmount: num(drug['opiateAmount']!), opiateUnit: txt(drug['opiateUnit']!),
          opiateSubstance: txt(drug['opiateSubstance']!), tablets: txt(drug['tablets']!),
          addictionYears: num(drug['addictionYears']!), addictionMonths: num(drug['addictionMonths']!),
        },
        record: { remarks: txt(record['remarks']!) },
        files: [photo, aadhaarFront, aadhaarBack].filter(Boolean).map(f => ({
          fileId: f!.fileId, kind: f!.kind, mimeType: f!.mimeType, sizeBytes: f!.sizeBytes,
        })),
        patientVersion: versions.patient,
        registrationVersion: versions.registration,
      })
      toast.ok('Record updated')
      navigate(`/patients/${id}`)
    } catch (e) {
      const err = e as ApiError
      if (err.code === 'VALIDATION_FAILED') {
        const next: Record<string, string> = {}
        for (const dt of err.details ?? []) next[dt.field.split('.').pop()!] = dt.message
        setErrors(next)
        toast.fail('Some fields need attention')
      } else if (err.code === 'STALE_WRITE') {
        toast.fail('Someone else saved first', err.message)
      } else {
        toast.fail('Could not save', err.message)
      }
    } finally { setBusy(false) }
  }

  if (loading) {
    return (
      <>
        <PageHeader title="Edit record" />
        <div className="content"><div className="page-max card"><div className="card-body stack" style={{ gap: 'var(--s-4)' }}>
          {Array.from({ length: 8 }, (_, i) => <Skeleton key={i} h={22} />)}
        </div></div></div>
      </>
    )
  }

  return (
    <>
      <PageHeader title={`Edit — ${patient['fullName'] ?? ''}`} />
      <div className="content">
        <form className="page-max card" style={{ maxWidth: 780 }} onSubmit={save}>
          <div className="card-body">
            <section className="form-section">
              <h3>Identification</h3>
              <div className="form-grid">
                <div className="span-2">
                  <Field label="Name" error={errors['fullName']}>
                    <Input {...p('fullName')} required maxLength={160}
                      aria-invalid={Boolean(errors['fullName'])} />
                  </Field>
                </div>
                <Field label="Father's name" error={errors['fatherName']}><Input {...p('fatherName')} maxLength={160} /></Field>
                <Field label="Age" error={errors['age']}><Input {...p('age')} type="number" min={0} max={130} /></Field>
                <Field label="Sex" error={errors['sex']}>
                  <Select {...p('sex')}><option value="">—</option>
                    {SEXES.map(x => <option key={x} value={x}>{x[0]!.toUpperCase() + x.slice(1)}</option>)}
                  </Select>
                </Field>
                <Field label="Blood group" error={errors['bloodGroup']}>
                  <Select {...p('bloodGroup')}><option value="">—</option>
                    {BLOOD_GROUPS.map(x => <option key={x} value={x}>{x}</option>)}
                  </Select>
                </Field>
                <Field label="Marital status" error={errors['maritalStatus']}><Input {...p('maritalStatus')} maxLength={40} /></Field>
                <Field label="Education" error={errors['education']}><Input {...p('education')} maxLength={80} /></Field>
                <Field label="Caste" error={errors['caste']}><Input {...p('caste')} maxLength={80} /></Field>
                <Field label="Occupation" error={errors['occupation']}><Input {...p('occupation')} maxLength={80} /></Field>
                <Field label="Phone" error={errors['phone']}>
                  <Input {...p('phone')} type="tel" maxLength={20} aria-invalid={Boolean(errors['phone'])} />
                </Field>
                <Field label="Pincode" error={errors['pincode']} hint="Six digits">
                  <Input {...p('pincode')} inputMode="numeric" maxLength={6}
                    aria-invalid={Boolean(errors['pincode'])} />
                </Field>
                <div className="span-2">
                  <Field label="Aadhaar number" error={errors['aadhaar']}
                    hint="Twelve digits. The check digit is verified locally.">
                    <Input {...p('aadhaar')} inputMode="numeric" maxLength={14}
                      placeholder="1234 5678 9012" aria-invalid={Boolean(errors['aadhaar'])} />
                  </Field>
                </div>
                <div className="span-2">
                  <Field label="Address" error={errors['addressLine1']}><Input {...p('addressLine1')} maxLength={240} /></Field>
                </div>
                <Field label="Tehsil" error={errors['tehsil']}><Input {...p('tehsil')} maxLength={80} /></Field>
                <Field label="District" error={errors['district']}><Input {...p('district')} maxLength={80} /></Field>
                <Field label="State" error={errors['state']}><Input {...p('state')} maxLength={80} /></Field>
              </div>
            </section>

            <section className="form-section">
              <h3>Photograph</h3>
              {existing['photo'] && !photo ? (
                <div className="row" style={{ gap: 'var(--s-5)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div className="photo-box"><img src={existing['photo']} alt="Current photo" /></div>
                  <div className="grow">
                    <CameraCapture kind="photo" label="Replace" value={photo} onChange={setPhoto} />
                  </div>
                </div>
              ) : (
                <CameraCapture kind="photo" value={photo} onChange={setPhoto} />
              )}
            </section>

            <section className="form-section">
              <h3>Aadhaar card</h3>
              <div className="capture-pair">
                <div>
                  {existing['aadhaar_front'] && !aadhaarFront ? (
                    <div className="doc-shot" style={{ marginBottom: 'var(--s-3)' }}>
                      <img src={existing['aadhaar_front']} alt="Current Aadhaar front" />
                    </div>
                  ) : null}
                  <CameraCapture kind="aadhaar_front" label="Front" aspect="8 / 5"
                    value={aadhaarFront} onChange={setAadhaarFront} />
                </div>
                <div>
                  {existing['aadhaar_back'] && !aadhaarBack ? (
                    <div className="doc-shot" style={{ marginBottom: 'var(--s-3)' }}>
                      <img src={existing['aadhaar_back']} alt="Current Aadhaar back" />
                    </div>
                  ) : null}
                  <CameraCapture kind="aadhaar_back" label="Back" aspect="8 / 5"
                    value={aadhaarBack} onChange={setAadhaarBack} />
                </div>
              </div>
            </section>

            <section className="form-section">
              <h3>Body measurement — at registration</h3>
              <div className="form-grid">
                <Field label="Present weight (kg)" error={errors['weightKg']} hint="Optional">
                  <Input {...m('weightKg')} type="number" step="0.1" min={0} />
                </Field>
                <div />
                <Field label="Blood pressure — systolic" error={errors['bpSystolic']} hint="Required">
                  <Input {...m('bpSystolic')} type="number" min={40} max={300} required
                    aria-invalid={Boolean(errors['bpSystolic'])} />
                </Field>
                <Field label="Blood pressure — diastolic" error={errors['bpDiastolic']} hint="Required">
                  <Input {...m('bpDiastolic')} type="number" min={20} max={200} required
                    aria-invalid={Boolean(errors['bpDiastolic'])} />
                </Field>
                <Field label="Pulse (per minute)" error={errors['pulse']} hint="Required">
                  <Input {...m('pulse')} type="number" min={20} max={250} required />
                </Field>
                <Field label="Oxygen — SpO₂ (%)" error={errors['spo2']} hint="Required">
                  <Input {...m('spo2')} type="number" min={50} max={100} required />
                </Field>
              </div>
            </section>

            <section className="form-section">
              <h3>Drug quantity</h3>
              <div className="form-grid">
                <div className="span-2">
                  <Field label="Quantity of opiate drug or other narcotics"
                    error={errors['opiateAmount']} hint="Reads as: 10 Kg per month, opium">
                    <div className="row" style={{ gap: 'var(--s-2)' }}>
                      <Input {...d('opiateAmount')} type="number" step="0.001" min={0}
                        style={{ flex: 2, minWidth: 0 }} />
                      <Select {...d('opiateUnit')} style={{ flex: 1, minWidth: 0 }} aria-label="Unit">
                        {OPIATE_UNITS.map(u => <option key={u} value={u}>{u === 'kg' ? 'Kg' : 'g'}</option>)}
                      </Select>
                      <span className="dim" style={{ whiteSpace: 'nowrap' }}>per month</span>
                      <Select {...d('opiateSubstance')} style={{ flex: 1.3, minWidth: 0 }} aria-label="Substance">
                        {OPIATE_SUBSTANCES.map(x => <option key={x} value={x}>{SUBSTANCE_LABEL[x]}</option>)}
                      </Select>
                    </div>
                  </Field>
                </div>
                <div className="span-2">
                  <Field label="Any tablet" error={errors['tablets']}><Input {...d('tablets')} maxLength={400} /></Field>
                </div>
                <Field label="Addiction period — years" error={errors['addictionYears']}>
                  <Input {...d('addictionYears')} type="number" min={0} max={120} />
                </Field>
                <Field label="Addiction period — months" error={errors['addictionMonths']} hint="0 to 11">
                  <Input {...d('addictionMonths')} type="number" min={0} max={11} />
                </Field>
              </div>
            </section>

            <section className="form-section">
              <h3>Remarks</h3>
              <div className="form-grid">
                <div className="span-2">
                  <Field label="Remarks" error={errors['remarks']}>
                    <Input {...r('remarks')} maxLength={1000} />
                  </Field>
                </div>
              </div>
            </section>
          </div>

          <div className="form-actions">
            <Button type="submit" variant="primary" loading={busy}>Save changes</Button>
            <Button type="button" variant="ghost" onClick={() => navigate(`/patients/${id}`)} disabled={busy}>
              Cancel
            </Button>
            <span className="grow" />
            <span className="dim" style={{ fontSize: 'var(--text-xs)' }}>
              Saved together in one transaction
            </span>
          </div>
        </form>
      </div>
    </>
  )
}
